<template>
  <div>
    <RadioGroup v-if="!isSuccess" v-model="reason" :orientation="'vertical'">
      <div>
        <div class="flex w-full items-center justify-between">
          <span class="text-l1 mb-2 font-bold">{{ t('comments.reportComment') }}</span>
          <CircleX
            :size="18"
            @click="commentStore.setReportCommentForm(false)"
            class="cursor-pointer"
          />
        </div>
        <div class="mb-2 flex flex-col">
          <span class="mb-1 flex text-xs font-bold">{{ commentStore.reportToCommentName }}</span>
          <span class="flex text-xs">{{ commentStore.reportToComment }}</span>
        </div>
      </div>
      <div
        v-for="reasonOption in reportReasons"
        :key="reasonOption.id"
        class="flex items-center space-x-2"
      >
        <RadioGroupItem :id="reasonOption.value" :value="reasonOption.value" />
        <Label :for="reasonOption.value">{{ t(reasonOption.label) }}</Label>
      </div>
      <Button :disabled="isReporting" class="mt-3 w-full" @click="reportComment">
        <Loader2 v-if="isReporting" class="mr-2 h-4 w-4 animate-spin stroke-white" />
        {{ t('comments.submitReport') }}</Button
      >
      <InfoBar class="mt-2" v-if="error" variant="destructive" :title="error" :message="''" />
    </RadioGroup>
    <div v-else class="text-center">
      <h3 class="mb-1 text-lg font-bold">{{ t('comments.reportSuccessful') }}</h3>
      <p class="text-xs text-gray-600">{{ t('comments.reportSuccessfulSubtitle') }}</p>
      <Button class="mt-4 w-full" @click="resetForm">{{ t('common.accept') }}</Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useCommentStore } from '@stores/comment.store';
import { useI18n } from 'vue-i18n';

// Components
import Button from '@ui/button/Button.vue';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import InfoBar from '@/components/generic/InfoBar.vue';

// Icons
import { CircleX, Loader2 } from 'lucide-vue-next';

// Composables
const { t } = useI18n();

// Stores
const commentStore = useCommentStore();

// Refs
const reason = ref(null);
const error = ref('');
const isSuccess = ref(false);
const reportReasons = ref([
  {
    id: 1,
    label: 'comments.reportReasons.spam',
    value: 'spam',
  },
  {
    id: 2,
    label: 'comments.reportReasons.bullying',
    value: 'bullying',
  },
  {
    id: 3,
    label: 'comments.reportReasons.violence',
    value: 'violence',
  },
  {
    id: 4,
    label: 'comments.reportReasons.sexual',
    value: 'sexual',
  },
  {
    id: 5,
    label: 'comments.reportReasons.selfharm',
    value: 'selfharm',
  },
]);

// Computed
const isReporting = computed(() => commentStore.commentReportisFetching);
const reportData = computed(() => commentStore.commentReportData);

// Methods
const reportComment = async () => {
  if (reason.value) {
    await commentStore.reportComment(commentStore.reportToId, reason.value);

    if (reportData.value) {
      reason.value = null;
      isSuccess.value = true;
      if (error.value) error.value = '';
    }
  } else {
    error.value = t('comments.reasonRequired');
  }
};

const resetForm = async () => {
  isSuccess.value = false;
  commentStore.clearReportData();
};
</script>

<style scoped></style>
